import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RotateCcw, Trash2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SaleInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // Conversion modal state
  const [showConvert, setShowConvert] = useState(false);
  const [convertItems, setConvertItems] = useState([]);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/sale-invoices/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
      } catch (err) { setError('Failed to load sale invoice'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // --- Conversion Logic ---
  const openConvertModal = () => {
    const items = (doc.line_items || []).map(li => ({
      product_id: li.product_id || '',
      description: li.description || '',
      mrp: li.mrp || '',
      quantity: li.quantity || 1,
      max_quantity: li.quantity || 1,
      unit_id: li.unit_id || '',
      price_per_unit: li.price_per_unit || 0,
      discount_percentage: li.discount_percentage || 0,
      discount_amount: li.discount_amount || 0,
      tax_id: li.tax_id || '',
      tax_amount: li.tax_amount || 0,
      total_amount: li.total_amount || 0,
    }));
    setConvertItems(items);
    setConvertError('');
    setShowConvert(true);
  };

  const recalcItem = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const ppu = parseFloat(item.price_per_unit) || 0;
    const discPct = parseFloat(item.discount_percentage) || 0;
    const base = qty * ppu;
    const discAmt = base * (discPct / 100);
    const afterDisc = base - discAmt;
    const tax = taxes.find(t => t.id === item.tax_id);
    const taxAmt = tax ? afterDisc * (tax.percentage / 100) : 0;
    return {
      ...item,
      discount_amount: Math.round(discAmt * 100) / 100,
      tax_amount: Math.round(taxAmt * 100) / 100,
      total_amount: Math.round((afterDisc + taxAmt) * 100) / 100,
    };
  };

  const updateConvertItem = (idx, field, value) => {
    setConvertItems(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], [field]: value };
      if (['quantity', 'price_per_unit', 'discount_percentage', 'tax_id'].includes(field)) {
        n[idx] = recalcItem(n[idx]);
      }
      return n;
    });
  };

  const removeConvertItem = (idx) => {
    setConvertItems(prev => prev.filter((_, i) => i !== idx));
  };

  const convertTotals = useMemo(() => {
    let subtotal = 0;
    convertItems.forEach(i => { subtotal += (parseFloat(i.total_amount) || 0); });
    const total = Math.round(subtotal);
    const roundOffAmt = Math.round((total - subtotal) * 100) / 100;
    return { subtotal: Math.round(subtotal * 100) / 100, total, roundOffAmt };
  }, [convertItems]);

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    setConvertError('');
    if (!convertItems.length) { setConvertError('Add at least one item for the return'); return; }
    setConvertSubmitting(true);
    try {
      const lineItems = convertItems.map(li => ({
        product_id: li.product_id || null,
        description: li.description || null,
        mrp: parseFloat(li.mrp) || null,
        quantity: parseFloat(li.quantity) || 1,
        unit_id: li.unit_id || null,
        price_per_unit: parseFloat(li.price_per_unit) || 0,
        discount_percentage: parseFloat(li.discount_percentage) || 0,
        discount_amount: parseFloat(li.discount_amount) || 0,
        tax_id: li.tax_id || null,
        tax_amount: parseFloat(li.tax_amount) || 0,
        total_amount: parseFloat(li.total_amount) || 0,
      }));

      const today = new Date().toISOString().slice(0, 10);
      const body = {
        customer_id: doc.customer_id || null,
        company_id: doc.company_id || null,
        invoice_number: doc.invoice_number || null,
        invoice_date: doc.invoice_date || null,
        date: today,
        state_of_supply: doc.state_of_supply || null,
        line_items: lineItems,
        payments: [{ payment_type: 'Cash', amount: 0 }],
        description: `Sale Return against Invoice ${doc.invoice_number || ''}`.trim(),
        round_off: true,
        round_off_amount: convertTotals.roundOffAmt,
        subtotal: convertTotals.subtotal,
        total: convertTotals.total,
      };

      await axios.post(`${BACKEND_URL}/api/credit-notes`, body, getAuthHeaders());
      navigate('/admin/sale/credit-notes');
    } catch (err) {
      setConvertError(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  return (
    <div data-testid="sale-invoice-detail-page">
      <button onClick={() => navigate('/admin/sale/invoices')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Sale Invoices
      </button>

      {/* Header + Convert Button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Sale Invoice Details</h1>
        <button onClick={openConvertModal} data-testid="convert-credit-note-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-red-700 transition-colors">
          <RotateCcw className="h-3.5 w-3.5" /> Convert to Sale Return / Credit Note
        </button>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice Number</span><span className="text-sm font-semibold text-slate-900">{doc.invoice_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice Date</span><span className="text-sm text-slate-900">{formatDate(doc.invoice_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Sale Type</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${doc.sale_type === 'Credit' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{doc.sale_type}</span>
          </div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Customer</span><span className="text-sm text-slate-900">{doc.customer_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.po_number && <div><span className="block text-xs font-medium text-slate-500 uppercase">PO Number</span><span className="text-sm text-slate-900">{doc.po_number}</span></div>}
          {doc.lut_arn_number && <div><span className="block text-xs font-medium text-slate-500 uppercase">LUT/ARN Number</span><span className="text-sm text-slate-900">{doc.lut_arn_number}</span></div>}
          {doc.billing_address && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Billing Address</span><span className="text-sm text-slate-900">{doc.billing_address}</span></div>}
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Received</span><span className="text-green-600 font-medium">₹{(doc.received_amount || 0).toLocaleString()}</span></div>
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Balance</span><span className={(doc.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}>₹{(doc.balance || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* ========== CONVERSION MODAL ========== */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-5xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                Convert to Sale Return / Credit Note
              </h2>
              <button onClick={() => setShowConvert(false)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit} className="p-6">
              {convertError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-5 w-5" /><span>{convertError}</span>
                </div>
              )}

              {/* Source info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                <div><span className="text-xs text-slate-500 block">Customer</span><span className="font-medium text-slate-900">{doc.customer_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Company</span><span className="font-medium text-slate-900">{doc.company_name || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">Source Invoice</span><span className="font-medium text-slate-900">{doc.invoice_number || '-'}</span></div>
                <div><span className="text-xs text-slate-500 block">State of Supply</span><span className="font-medium text-slate-900">{doc.state_of_supply || '-'}</span></div>
              </div>

              <p className="text-xs text-slate-500 mb-3">Adjust return quantities or remove items you don't want to return. Quantity cannot exceed original invoice quantity.</p>

              {/* Editable Line Items */}
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-2 py-2 text-left w-8">#</th>
                      <th className="px-2 py-2 text-left min-w-[180px]">Product</th>
                      <th className="px-2 py-2 text-left min-w-[120px]">Description</th>
                      <th className="px-2 py-2 text-right w-20">Orig Qty</th>
                      <th className="px-2 py-2 text-right w-24">Return Qty</th>
                      <th className="px-2 py-2 text-left w-24">Unit</th>
                      <th className="px-2 py-2 text-right w-24">Price/Unit</th>
                      <th className="px-2 py-2 text-right w-16">Disc %</th>
                      <th className="px-2 py-2 text-left w-32">Tax</th>
                      <th className="px-2 py-2 text-right w-24">Amount</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {convertItems.map((li, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-slate-400">{idx + 1}</td>
                        <td className="px-2 py-2 font-medium text-slate-900 text-sm">
                          {getName(products, li.product_id)}
                        </td>
                        <td className="px-2 py-2 text-slate-600 text-sm">{li.description || '-'}</td>
                        <td className="px-2 py-2 text-right text-slate-500">{li.max_quantity}</td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.quantity}
                            onChange={e => {
                              let val = parseFloat(e.target.value);
                              if (val > li.max_quantity) val = li.max_quantity;
                              updateConvertItem(idx, 'quantity', val);
                            }}
                            min="0.01" max={li.max_quantity} step="0.01" data-testid={`conv-qty-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-2 py-2 text-sm text-slate-600">{getName(units, li.unit_id)}</td>
                        <td className="px-2 py-2 text-right text-sm">{(parseFloat(li.price_per_unit) || 0).toFixed(2)}</td>
                        <td className="px-2 py-2 text-right text-sm">{li.discount_percentage}%</td>
                        <td className="px-2 py-2 text-sm">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                        <td className="px-2 py-2 text-right font-medium text-slate-900" data-testid={`conv-total-${idx}`}>
                          {(parseFloat(li.total_amount) || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => removeConvertItem(idx)} data-testid={`conv-remove-${idx}`}
                            className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                    {convertItems.length === 0 && (
                      <tr><td colSpan={11} className="text-center py-6 text-slate-400">All items removed. Add at least one item to proceed.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="border-t border-slate-200 pt-4 mb-4">
                <div className="max-w-xs ml-auto space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Return Subtotal</span><span className="font-medium">₹{convertTotals.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span>{convertTotals.roundOffAmt >= 0 ? '+' : ''}{convertTotals.roundOffAmt.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2"><span>Credit Note Total</span><span>₹{convertTotals.total.toLocaleString()}</span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowConvert(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={convertSubmitting || !convertItems.length} data-testid="conv-submit-btn"
                  className="px-6 py-2 bg-red-600 text-white font-bold uppercase tracking-wider rounded-none hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create Credit Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
